import type { TourEvent } from '@/features/events';
import { formatDateRangeLong } from '@/features/events';
import { Section } from '@/components/layout';
import { ResponsiveImage } from '@/components/media';
import { TicketCta } from '@/components/ui';
import { VipCrown } from '@/components/ornaments';
import { Reveal } from '@/components/motion';
import { cancunMedia } from '../media';
import { CANCUN_COPY } from '../copy';
import styles from './StripPremiumCancun.module.css';

const TITLE_ID = 'cancun-experiencia';

export interface StripPremiumCancunProps {
  event: TourEvent;
}

/**
 * S3 · STRIP "EXPERIENCIA PREMIUM · COMITÉ ÍNTIMO" — banda y 1138-1278.
 * Fotografía de playa a sangre, la franja de decisión de la página.
 *
 * El mockup rotula "Lugares limitados" en la segunda mitad. DECISIÓN DEL
 * ORACLE (20260726): se retira. Afirma una escasez (aforo) que el cliente no
 * ha confirmado — es justo la categoría que `CLAUDE.md` línea roja 6 prohíbe
 * ("plazas restantes" = falsa escasez), aunque ADR-003 §4 la hubiera permitido
 * antes. Se usa la misma formulación honesta que ya lleva el hero: posiciona
 * la exclusividad sin inventar un número ni una urgencia. Ver
 * `docs/VISUAL_DEVIATIONS.md` §1.23.
 *
 * El CTA es la pieza más cara de la web: oro con doble filete y texto navy —
 * el contraste navy-sobre-oro es lo que lo hace caro, no un blanco genérico.
 * Su comportamiento lo decide `salesState`, no esta página.
 */
export function StripPremiumCancun({ event }: StripPremiumCancunProps) {
  const [premium, limited] = CANCUN_COPY.strip.claim;

  return (
    <Section
      width="full"
      spacing="flush"
      background="none"
      labelledBy={TITLE_ID}
      className={styles.strip}
      ornament={
        <ResponsiveImage
          asset={cancunMedia.beachStrip}
          sizes="100vw"
          className={styles.photo}
          decorative
        />
      }
    >
      <Reveal className={styles.body}>
        <h2 id={TITLE_ID} className={styles.title}>
          <span>{premium}</span>
          <span className={styles.dot} aria-hidden="true">
            ·
          </span>
          <span>{limited}</span>
        </h2>

        <div className={styles.ctaRow}>
          <TicketCta
            event={event}
            variant="premium"
            size="lg"
            className={styles.cta}
            icon={<VipCrown size={28} className={styles.crown} />}
          />
        </div>

        {/* O-13, replicado desde Buenos Aires. Aquí la primera frase dice
            «plazas» y no «entradas»: Cancún no vende una entrada a una sala,
            vende una plaza en una convivencia de aforo corto. */}
        <p className={styles.lead}>
          {formatDateRangeLong(event)}, en {event.city}. Nos vemos allí.
        </p>
        <p className={styles.nota}>Reserva tu plaza en la tienda oficial de la gira.</p>
      </Reveal>
    </Section>
  );
}
